import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { UsersRepository } from '../../data/repositories';
import {
  HabitaButton,
  HabitaDialog,
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaSearchInput,
  HabitaSelect,
  HabitaTextInput,
  classNames,
  habitaControlBorderClass,
  habitaSurfaceClass,
} from '../../shared/components/habita';
import type {
  PlatformUser,
  UserAccessProfile,
  UserStatus,
} from '../../shared/models';
import {
  USER_ACCESS_PROFILE_LABELS,
  USER_ACCESS_PROFILES,
  USER_STATUS_LABELS,
} from '../../shared/models';

const ALL = '__all__';

interface UserFormState {
  email: string;
  name: string;
  profile: UserAccessProfile;
  status: UserStatus;
}

const emptyForm: UserFormState = {
  email: '',
  name: '',
  profile: 'SDR',
  status: 'ACTIVE',
};

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function statusBadgeClass(status: UserStatus): string {
  return status === 'ACTIVE'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-200'
    : 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-500/40 dark:bg-slate-400/10 dark:text-slate-200';
}

function profileBadgeClass(profile: UserAccessProfile): string {
  if (profile === 'ADMIN') return 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-400/40 dark:bg-violet-400/10 dark:text-violet-200';
  if (profile === 'GERENTE') return 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-400/40 dark:bg-sky-400/10 dark:text-sky-200';
  if (profile === 'SUPERVISOR') return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200';
  return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-200';
}

function UserBadge({
  children,
  className,
}: {
  children: string;
  className: string;
}) {
  return (
    <span className={classNames('inline-flex rounded-md border px-2.5 py-0.5 font-instrument text-xs font-medium', className)}>
      {children}
    </span>
  );
}

export default function Usuarios() {
  const usersRepository = useMemo(() => new UsersRepository(), []);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [profileFilter, setProfileFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [form, setForm] = useState<UserFormState>(emptyForm);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setUsers(await usersRepository.fetchUsers());
    } catch {
      setLoadError('Não foi possível carregar os usuários.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [usersRepository]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const editingUser = useMemo(
    () => users.find((user) => user.id === editingUserId) ?? null,
    [editingUserId, users]
  );
  const filteredUsers = useMemo(() => {
    const query = normalizeSearch(searchTerm);

    return users.filter((user) => {
      const matchesQuery = !query || (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
      const matchesProfile = profileFilter === ALL || user.profile === profileFilter;
      const matchesStatus = statusFilter === ALL || user.status === statusFilter;

      return matchesQuery && matchesProfile && matchesStatus;
    });
  }, [profileFilter, searchTerm, statusFilter, users]);
  const hasFilters = searchTerm.trim() !== '' || profileFilter !== ALL || statusFilter !== ALL;

  function openCreateDialog() {
    setEditingUserId(null);
    setForm(emptyForm);
    setFormError(null);
    setIsDialogOpen(true);
  }

  function openEditDialog(user: PlatformUser) {
    setEditingUserId(user.id);
    setForm({
      email: user.email,
      name: user.name,
      profile: user.profile,
      status: user.status,
    });
    setFormError(null);
    setIsDialogOpen(true);
  }

  function closeDialog() {
    setIsDialogOpen(false);
    setEditingUserId(null);
    setForm(emptyForm);
    setFormError(null);
  }

  function clearFilters() {
    setSearchTerm('');
    setProfileFilter(ALL);
    setStatusFilter(ALL);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) {
      setFormError('Informe nome e e-mail.');
      return;
    }
    if (!isValidEmail(email)) {
      setFormError('Informe um e-mail válido.');
      return;
    }

    try {
      setFormError(null);
      if (editingUserId) {
        await usersRepository.updateUser(editingUserId, {
          email,
          name,
          profile: form.profile,
          status: form.status,
        });
      } else {
        await usersRepository.createUser({
          email,
          name,
          profile: form.profile,
        });
      }

      closeDialog();
      await loadUsers();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Não foi possível salvar o usuário.');
    }
  }

  return (
    <section className="w-full space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-fraunces text-2xl font-semibold text-textPrimary">Usuários</h1>
          <p className="mt-1 max-w-2xl font-instrument text-sm text-textSecondary">
            Cadastre e mantenha os acessos da plataforma.
          </p>
        </div>
        <HabitaButton icon="ri-user-add-line" onClick={openCreateDialog} variant="primary">
          Cadastrar usuário
        </HabitaButton>
      </div>

      {loadError && <HabitaErrorNotice>{loadError}</HabitaErrorNotice>}

      <div className="flex flex-wrap items-center gap-2">
        <HabitaSearchInput
          value={searchTerm}
          onValueChange={setSearchTerm}
          placeholder="Buscar por nome ou e-mail"
          containerClassName="min-w-[240px] max-w-md flex-1"
          inputClassName="h-9 bg-card text-textPrimary"
        />
        <HabitaSelect
          value={profileFilter}
          onChange={(event) => setProfileFilter(event.target.value)}
          options={[
            { label: 'Perfil', value: ALL },
            ...USER_ACCESS_PROFILES.map((profile) => ({
              label: USER_ACCESS_PROFILE_LABELS[profile],
              value: profile,
            })),
          ]}
          className="w-40"
          aria-label="Perfil"
        />
        <HabitaSelect
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          options={[
            { label: 'Status', value: ALL },
            { label: USER_STATUS_LABELS.ACTIVE, value: 'ACTIVE' },
            { label: USER_STATUS_LABELS.INACTIVE, value: 'INACTIVE' },
          ]}
          className="w-36"
          aria-label="Status"
        />
        {hasFilters && (
          <HabitaButton icon="ri-close-line" onClick={clearFilters} size="sm" variant="ghost">
            Limpar
          </HabitaButton>
        )}
        <span className="ml-auto font-instrument text-xs text-textSecondary">
          {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário' : 'usuários'}
        </span>
      </div>

      {loading ? (
        <div className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex animate-pulse items-center gap-4 border-b border-border-subtle px-4 py-3.5 last:border-b-0">
              <div className="h-4 w-48 rounded bg-muted" />
              <div className="h-4 flex-1 rounded bg-muted" />
              <div className="h-6 w-24 rounded bg-muted/70" />
              <div className="h-6 w-20 rounded bg-muted/70" />
            </div>
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <HabitaEmptyState
          icon="ri-user-search-line"
          title="Nenhum usuário encontrado"
          description={hasFilters ? 'Ajuste os filtros para ampliar a busca.' : 'Cadastre o primeiro usuário para começar.'}
        />
      ) : (
        <div className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left font-instrument text-sm">
              <thead className={`border-b ${habitaControlBorderClass} bg-muted text-xs text-textSecondary`}>
                <tr>
                  <th className="px-4 py-2.5 font-medium">Nome</th>
                  <th className="px-4 py-2.5 font-medium">E-mail</th>
                  <th className="px-4 py-2.5 font-medium">Perfil</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Atualizado</th>
                  <th className="px-4 py-2.5 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="transition-colors hover:bg-accent">
                    <td className="min-w-56 px-4 py-2.5 font-medium text-textPrimary">{user.name}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-textSecondary">{user.email}</td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <UserBadge className={profileBadgeClass(user.profile)}>
                        {USER_ACCESS_PROFILE_LABELS[user.profile]}
                      </UserBadge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <UserBadge className={statusBadgeClass(user.status)}>
                        {USER_STATUS_LABELS[user.status]}
                      </UserBadge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-textSecondary">
                      {user.updated_at ? new Date(user.updated_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      <HabitaButton icon="ri-pencil-line" onClick={() => openEditDialog(user)} size="sm" variant="ghost">
                        Editar
                      </HabitaButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <HabitaDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        title={editingUser ? 'Editar usuário' : 'Cadastrar usuário'}
        description={editingUser ? 'Atualize os dados e permissões do usuário.' : 'Informe os dados de acesso do usuário.'}
        footer={(
          <>
            <HabitaButton onClick={closeDialog} variant="secondary">Cancelar</HabitaButton>
            <HabitaButton form="user-admin-form" type="submit" variant="primary">Salvar</HabitaButton>
          </>
        )}
      >
        <form id="user-admin-form" className="space-y-4" onSubmit={handleSubmit}>
          {formError && <HabitaErrorNotice>{formError}</HabitaErrorNotice>}

          <label className="block space-y-1">
            <span className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">Nome</span>
            <HabitaTextInput
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Nome completo"
            />
          </label>

          <label className="block space-y-1">
            <span className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">E-mail</span>
            <HabitaTextInput
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="email@habita4u.com"
            />
          </label>

          <label className="block space-y-1">
            <span className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">Perfil</span>
            <HabitaSelect
              value={form.profile}
              onChange={(event) => setForm((current) => ({ ...current, profile: event.target.value as UserAccessProfile }))}
              options={USER_ACCESS_PROFILES.map((profile) => ({
                label: USER_ACCESS_PROFILE_LABELS[profile],
                value: profile,
              }))}
              className="w-full"
            />
          </label>

          {editingUser ? (
            <label className={`flex items-center justify-between gap-4 rounded-md border ${habitaControlBorderClass} bg-muted/40 px-3 py-3`}>
              <span>
                <span className="block font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">Status</span>
                <span className="mt-0.5 block font-instrument text-sm text-textPrimary">{USER_STATUS_LABELS[form.status]}</span>
              </span>
              <input
                type="checkbox"
                checked={form.status === 'ACTIVE'}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  status: event.target.checked ? 'ACTIVE' : 'INACTIVE',
                }))}
                className="h-5 w-5 rounded border-border-subtle accent-primary"
                aria-label="Status ativo"
              />
            </label>
          ) : (
            <div className="flex items-center gap-2">
              <UserBadge className={statusBadgeClass('ACTIVE')}>Ativo</UserBadge>
              <span className="font-instrument text-sm text-textSecondary">Novos usuários entram ativos.</span>
            </div>
          )}
        </form>
      </HabitaDialog>
    </section>
  );
}
